import { ChannelDomainModel } from '../../../../domain/currently-on-tv-tray';
import { Channel } from '../../../../domain/channel';
import { PlatformType } from 'osn-shared';
import { MarketingItemLayout } from '../../../../shared/enums/marketing.enums';
import { TraySelectorType } from '../../tray-selector/tray-selector.enum';
import { SchedulingControlViewModel } from '../../scheduling-control/scheduling-control-view-model';
import {
  AllPlatformLocalizedImagesViewModel,
  BackgroundImageViewModel,
} from '../../../../shared/components/image-upload-control/image-upload-view-model';
import { LocalizedDomainImages, ImageData } from '../../../../domain/localized-domain-images';
import { MarketingTrayButton, MarketingTrayButtonType } from 'projects/osn-cxb/src/app/domain/marketing-tray-button';
import { MarketingButtonFormViewModel } from '../../marketing-button-control/marketing-button-form-view-model';

export const nameLocalizedMapperViewModelToDomain = <T extends { titleEnglish?: string; titleArabic?: string }>(
  tray: T
): { titleEnglish: string; titleArabic: string } => {
  return {
    titleEnglish: tray.titleEnglish || '',
    titleArabic: tray.titleArabic || '',
  };
};

export const commonMapperViewModelToDomain = <T extends { id?: string; platformIds?: string[] }>(
  tray: T
): { id: string; order: number; visible: boolean; draggable: boolean; platformIds: string[] } => {
  return {
    id: tray.id || '',
    order: undefined,
    visible: undefined,
    draggable: true,
    platformIds: tray.platformIds,
  };
};

export const scheduledDateMapperViewModelToDomain = <T extends { scheduling: SchedulingControlViewModel }>(
  tray: T
): { scheduledFrom: number; scheduledTo: number } => {
  return {
    scheduledFrom: tray.scheduling.scheduledFrom ? new Date(tray.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: tray.scheduling.scheduledTo ? new Date(tray.scheduling.scheduledTo).getTime() : undefined,
  };
};

export const scheduledDateMapperViewModelToApi = <T extends { scheduling: SchedulingControlViewModel }>({
  scheduling: { scheduledFrom, scheduledTo },
}: T): { scheduledFrom: string; scheduledTo: string } => {
  return {
    scheduledFrom: scheduledFrom ? new Date(scheduledFrom).toISOString() : undefined,
    scheduledTo: scheduledTo ? new Date(scheduledTo).toISOString() : undefined,
  };
};

export const mapViewModelImagesToLocalizedDomainImages = <T extends Partial<AllPlatformLocalizedImagesViewModel>>(
  imagesVm: T,
  platformIds: Record<PlatformType, string>,
  order?: number
): LocalizedDomainImages => {
  const domainImageModels = { en: [], ar: [], order: undefined };
  Object.values(PlatformType).forEach((platform) => {
    if (!imagesVm[platform]) {
      return;
    }

    domainImageModels.en.push(mapViewModelImageToDomainImage(imagesVm[platform].en, platform as PlatformType, platformIds));
    if (imagesVm[platform].ar) {
      domainImageModels.ar.push(mapViewModelImageToDomainImage(imagesVm[platform].ar, platform as PlatformType, platformIds));
    }
  });
  domainImageModels.order = order;
  return domainImageModels as LocalizedDomainImages;
};

const mapViewModelImageToDomainImage = (
  vmImage: BackgroundImageViewModel,
  platform: PlatformType,
  platformIds: Record<PlatformType, string>
): ImageData => {
  return {
    id: vmImage.id,
    name: vmImage.name,
    publicUrl: vmImage.publicUrl,
    platformId: platformIds[platform],
    platformName: platform,
  };
};

export const mapViewModelImageToDomainImageWithoutPlatforms = (vmImage: BackgroundImageViewModel): ImageData => {
  return {
    id: vmImage.id,
    name: vmImage.name,
    publicUrl: vmImage.publicUrl,
  };
};

export const mapViewModelImagesToLocalizedDomainImagesWithoutPlatforms = <T extends Partial<AllPlatformLocalizedImagesViewModel>>(
  imagesVm: T,
  order?: number
): LocalizedDomainImages => {
  const domainImageModels = { en: [], ar: [], order: undefined };
  Object.keys(imagesVm).forEach((platform) => {
    domainImageModels.en.push(mapViewModelImageToDomainImageWithoutPlatforms(imagesVm[platform].en));
    if (imagesVm[platform].ar) {
      domainImageModels.ar.push(mapViewModelImageToDomainImageWithoutPlatforms(imagesVm[platform].ar));
    }
  });
  domainImageModels.order = order;
  return domainImageModels as LocalizedDomainImages;
};

export const mapViewModelImagesToDomainImages = <T extends Partial<AllPlatformLocalizedImagesViewModel>>(
  imagesVm: T,
  platformIds
): ImageData[] => {
  const domainImageModels = [];
  Object.keys(imagesVm).forEach((platform) => {
    domainImageModels.push(mapViewModelImageToDomainImage(imagesVm[platform].en, platform as PlatformType, platformIds));
    if (imagesVm[platform].ar) {
      domainImageModels.push(mapViewModelImageToDomainImage(imagesVm[platform].ar, platform as PlatformType, platformIds));
    }
  });
  return domainImageModels;
};

export const mapMarketingButtonToDomainButton = (
  button: MarketingButtonFormViewModel,
  buttonType: MarketingTrayButtonType = MarketingTrayButtonType.Primary
): MarketingTrayButton => {
  return {
    buttonType,
    action: button.action,
    buttonTextEnglish: button.buttonTextEnglish,
    buttonTextArabic: button.buttonTextArabic,
    buttonActionPageTargetId: button.actionPageTargetId,
    buttonActionExternalUrl: button.actionExternalUrl,
    buttonActionProgramTarget: button.actionProgramTarget
      ? {
          availabilityFrom: button.actionProgramTarget.availabilityFrom,
          availabilityTo: button.actionProgramTarget.availabilityTo,
          id: button.actionProgramTarget.id,
          title: button.actionProgramTarget.title,
          episode: button.actionProgramTarget.episode,
          season: button.actionProgramTarget.season,
        }
      : undefined,
  };
};

export const mapTraySelectorTypeToMarketingItemLayout = (layout: TraySelectorType): MarketingItemLayout => {
  switch (layout) {
    case TraySelectorType.MarketingCTALeft:
      return MarketingItemLayout.CTA_LEFT;
    case TraySelectorType.MarketingCTARight:
      return MarketingItemLayout.CTA_RIGHT;
    case TraySelectorType.MarketingTrendingLeft:
      return MarketingItemLayout.TRENDING_LEFT;
    case TraySelectorType.MarketingTrendingRight:
      return MarketingItemLayout.TRENDING_RIGHT;
    case TraySelectorType.MarketingEmailCollectorCentered:
      return MarketingItemLayout.EMAIL_CENTERED;
    case TraySelectorType.MarketingEmailCollectorLeft:
      return MarketingItemLayout.EMAIL_LEFT;
    case TraySelectorType.MarketingEmailCollectorRight:
      return MarketingItemLayout.EMAIL_RIGHT;
    case TraySelectorType.MarketingMultipleContentLandscape:
      return MarketingItemLayout.MULTIPLE_CONTENT_LANDSCAPE;
    case TraySelectorType.MarketingMultipleContentPortrait:
      return MarketingItemLayout.MULTIPLE_CONTENT_PORTRAIT;
    case TraySelectorType.DetailedPageBannerLeft:
      return MarketingItemLayout.DETAILED_PAGE_BANNER_LEFT;
    case TraySelectorType.DetailedPageBannerCenter:
      return MarketingItemLayout.DETAILED_PAGE_BANNER_CENTER;
    case TraySelectorType.DetailedPageBannerRight:
      return MarketingItemLayout.DETAILED_PAGE_BANNER_RIGHT;
    default:
      return undefined;
  }
};

export const mapChannelViewModelToDomainModel = (
  channel: { id: string; order: number; name: string; channel: Channel },
  index: number
): ChannelDomainModel => {
  return {
    id: channel.id,
    order: index + 1,
    channel: {
      channelID: channel.channel.channelID,
      channelName: channel.channel.channelName,
      channelNumber: channel.channel.channelNumber,
      id: channel.channel.id,
    },
  };
};
